/**
 * Typed i18n core (XTRA-05, Language Manager round 3) — no framework.
 *
 * Resolution + locale normalisation are ported from the sibling EMS
 * Dashboard's i18n layer (TB-EMS ems-dashboard
 * apps/dashboard-app/src/i18n/index.ts): `normaliseLocale` accepts the
 * TB-shaped values seen in the wild ("en_US", "en-US", "EN", "it_IT",
 * "es-MX") and maps them onto a supported primary subtag, or null.
 *
 * Active-language resolution order:
 *   1. `?lang=` URL query param   (host widget / deep-link / dev override)
 *   2. localStorage "ems-lang"    (the operator's explicit Globe-menu pick)
 *   3. `navigator.language`       (browser default)
 *   4. "en"                       (hard fallback — always works)
 *
 * Rules:
 *  - Keys are "<ns>.<slug>". Each dict file under src/lib/i18n/ owns one
 *    namespace and exports DICT_<NS>; this module merges them. A key
 *    missing from the active language falls back to English, then to the
 *    key itself — the UI can never render blank because of a gap.
 *  - `useT()` is the React entry point (re-renders on language change);
 *    `tr()` is the imperative twin for non-hook call sites (toasts,
 *    confirm() texts). Both do `{name}` placeholder substitution.
 *  - `setLanguage()` persists to localStorage "ems-lang" and flips
 *    `<html lang>` so screen readers and hyphenation follow. The `?lang=`
 *    param is deliberately NOT persisted — it is a per-load override.
 *  - NEVER call tr() at module init: module-level consts evaluate before
 *    a host-supplied ?lang= can matter and never re-render. Keep consts
 *    as keys (or raw English) and translate at the render site.
 */

import { useCallback } from "react";
import { create } from "zustand";
import { DICT_COMMON } from "@/lib/i18n/dict.common";
import { DICT_MONITOR } from "@/lib/i18n/dict.monitor";
import { DICT_MANAGE } from "@/lib/i18n/dict.manage";
import { DICT_ANALYZE } from "@/lib/i18n/dict.analyze";
import { DICT_AVAILABILITY } from "@/lib/i18n/dict.availability";

export const SUPPORTED_LOCALES = ["en", "it", "de", "es"] as const;
export type Lang = (typeof SUPPORTED_LOCALES)[number];

/**
 * One dictionary entry. English is the source of truth and mandatory;
 * Italian is mandatory too (the deployed sites' operators are Italian);
 * German/Spanish are optional and fall back to English.
 */
export type DictEntry = { en: string; it: string; de?: string; es?: string };
/** Shape every per-namespace dict file must export (DICT_<NS>: Dict). */
export type Dict = Record<string, DictEntry>;

/** The merged dictionary — all namespaces flat-merged at module load. */
const DICT: Dict = {
  ...DICT_COMMON,
  ...DICT_MONITOR,
  ...DICT_MANAGE,
  ...DICT_ANALYZE,
  ...DICT_AVAILABILITY,
};

const STORAGE_KEY = "ems-lang";

/**
 * Normalise any BCP-47-ish / POSIX locale string to one of our supported
 * primary subtags, or null if unsupported. Ported from the EMS Dashboard
 * (TB-EMS) i18n/index.ts.
 *
 *   "en_US" | "en-GB" | "EN" | "en" → "en";  "es-MX" → "es";  "fr" → null
 */
export function normaliseLocale(raw: string | null | undefined): Lang | null {
  if (!raw) return null;
  const primary = raw.toLowerCase().split(/[-_]/)[0];
  if (!primary) return null;
  return (SUPPORTED_LOCALES as readonly string[]).includes(primary) ? (primary as Lang) : null;
}

function detectLang(): Lang {
  let urlLang: string | null = null;
  try {
    if (typeof window !== "undefined") {
      urlLang = new URLSearchParams(window.location.search).get("lang");
    }
  } catch { /* malformed URL — ignore */ }
  let stored: string | null = null;
  try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* sandboxed storage */ }
  const nav = typeof navigator !== "undefined" ? navigator.language : null;
  return normaliseLocale(urlLang) ?? normaliseLocale(stored) ?? normaliseLocale(nav) ?? "en";
}

/**
 * Tiny zustand store so useT()/useLanguage() consumers re-render when the
 * operator switches language — module-level `let` could not do that.
 */
const useLangStore = create<{ lang: Lang }>(() => ({ lang: detectLang() }));

/** Current language, imperatively (CSV filenames, document titles, …). */
export function currentLanguage(): Lang {
  return useLangStore.getState().lang;
}

/** React hook — current language; re-renders the consumer on change. */
export function useLanguage(): Lang {
  return useLangStore((s) => s.lang);
}

/** Switch language, persist it, and flip <html lang>. Hook consumers re-render. */
export function setLanguage(next: Lang) {
  const lang = normaliseLocale(next) ?? "en";
  useLangStore.setState({ lang });
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* sandboxed storage */ }
  if (typeof document !== "undefined") document.documentElement.lang = lang;
}

/**
 * Substitute `{name}` placeholders with values from `params`. Missing
 * placeholders are left as-is so they're easy to spot during development.
 */
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) =>
    Object.hasOwn(params, k) ? String(params[k]) : `{${k}}`,
  );
}

function translate(lang: Lang, key: string, params?: Record<string, string | number>): string {
  const entry = DICT[key];
  const template = entry ? (entry[lang] ?? entry.en) : key;
  return interpolate(template, params);
}

/**
 * Imperative translate in the CURRENT language — for toasts, confirm
 * texts and other non-hook call sites. Render-time UI should prefer
 * useT() so it re-renders on language change.
 */
export function tr(key: string, params?: Record<string, string | number>): string {
  return translate(useLangStore.getState().lang, key, params);
}

/**
 * React hook — returns a `t(key, params?)` bound to the active language.
 * The returned function is referentially stable per language (useCallback
 * over [lang]), so it is safe in useMemo/useEffect dependency lists.
 */
export function useT(): (key: string, params?: Record<string, string | number>) => string {
  const lang = useLanguage();
  return useCallback(
    (key: string, params?: Record<string, string | number>) => translate(lang, key, params),
    [lang],
  );
}

// Reflect the detected language on <html lang> at module load (index.html
// hardcodes lang="en"; Italian users get correct hyphenation/a11y).
if (typeof document !== "undefined") document.documentElement.lang = currentLanguage();
